import { App, Button, TimePicker, Typography } from 'antd';
import type { RangePickerProps } from 'antd/lib/date-picker';
import dayjs from 'dayjs';
import { type FC, useEffect, useState } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { AppModal } from '@/components/ui/AppModal';
import { formatError, logError } from '@/utils/formatError';
import {
  useGetDriverWindowSlots,
  useInsertDefaultDriverWindowSlots,
  useInsertDriverWindowSlot,
  useInsertWeekdayWindows,
  useUpdateDriverWindowProviders,
  useUpdateDriverWindowSlot,
} from '../../query/calendarGrid.hooks';
import { ProviderSelect, useDefaultDtkProvider } from './ProviderSelect';
import { useCalendarState } from './useCalendarState';

interface SlotModalProps {
  open: boolean;
  close: () => void;
}

export const SlotModal: FC<SlotModalProps> = ({ open, close }) => {
  const { message } = App.useApp();
  const { state, setIsEditMode, setSelectedTimeRange } = useCalendarState();
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const defaultDtkProvider = useDefaultDtkProvider();

  const { mutateAsync: insertDriverWindowSlot, isPending: insertDriverWindowSlotLoading } =
    useInsertDriverWindowSlot();
  const { mutateAsync: updateDriverWindowSlot, isPending: updateDriverWindowSlotLoading } =
    useUpdateDriverWindowSlot();

  const { mutateAsync: insertWeekdayWindows } = useInsertWeekdayWindows();

  const { mutateAsync: updateDriverWindowProviders, isPending: updateProvidersLoading } =
    useUpdateDriverWindowProviders();

  const { mutateAsync: insertDefaultDriverWindowSlots, isPending: insertDefaultLoading } =
    useInsertDefaultDriverWindowSlots();

  const { data: driverWindowSlotsData } = useGetDriverWindowSlots({
    driverId: state.selectedDriverId ?? '',
  });

  const { selectedDepartment } = useDepartments();

  useEffect(() => {
    if (state.isEditMode && state.selectedSlotId && driverWindowSlotsData?.driver_window) {
      const slot = driverWindowSlotsData.driver_window.find((s) => s.id === state.selectedSlotId);
      const weekdayWindow = slot?.weekday_windows?.[0] as
        | { window_delivery_providers?: Array<{ provider_id: string }> }
        | undefined;

      if (
        weekdayWindow?.window_delivery_providers &&
        weekdayWindow.window_delivery_providers.length > 0
      ) {
        const existingProviders = weekdayWindow.window_delivery_providers.map((p) => p.provider_id);
        setSelectedProviders(existingProviders);
      } else if (defaultDtkProvider) {
        setSelectedProviders([defaultDtkProvider]);
      }
    }
  }, [state.isEditMode, state.selectedSlotId, driverWindowSlotsData, defaultDtkProvider]);

  useEffect(() => {
    if (!state.isEditMode && open && defaultDtkProvider && selectedProviders.length === 0) {
      setSelectedProviders([defaultDtkProvider]);
    }
  }, [state.isEditMode, open, defaultDtkProvider, selectedProviders.length]);

  const handleTimeRangeChange: RangePickerProps['onChange'] = (values) => {
    setSelectedTimeRange(values?.[0] && values[1] ? [values[0], values[1]] : null);
  };

  useEffect(() => {
    if (selectedDepartment?.countryId === 'PE' && !state.selectedTimeRange) {
      const base = dayjs().startOf('day');
      setSelectedTimeRange([base.hour(8).minute(30), base.hour(18).minute(0)]);
    }
  }, [selectedDepartment, state.selectedTimeRange, setSelectedTimeRange]);

  const handleClose = () => {
    setSelectedProviders([]);
    close();
    setSelectedTimeRange(null);
    setIsEditMode(false);
  };

  const handleOk = async () => {
    if (state.selectedTimeRange) {
      const formattedTimeSlot = `${state.selectedTimeRange[0].format(
        'HH:mm'
      )}-${state.selectedTimeRange[1].format('HH:mm')}`;
      try {
        if (state.isEditMode && state.selectedSlotId) {
          await updateDriverWindowSlot({
            id: state.selectedSlotId,
            timeDuration: formattedTimeSlot,
          });

          const slot = driverWindowSlotsData?.driver_window?.find(
            (s) => s.id === state.selectedSlotId
          );
          if (slot?.weekday_windows) {
            const weekdayWindowIds = slot.weekday_windows.map((ww) => ww.id);
            await updateDriverWindowProviders({
              weekdayWindowIds,
              providerIds: selectedProviders,
            });
          }

          message.success('Time slot updated successfully');
        } else {
          const data = await insertDriverWindowSlot({
            driverId: state.selectedDriverId ?? '',
            timeDuration: formattedTimeSlot,
          });
          const driverWindowId = data?.insert_driver_window_one?.id;
          if (driverWindowId) {
            const weekdayWindowsResult = await insertWeekdayWindows({
              objects: Array.from({ length: 7 }, (_, i) => ({
                driverWindowId,
                weekday: i + 1,
              })),
            });

            const createdWindowIds =
              weekdayWindowsResult?.insert_weekday_window?.returning?.map((ww) => ww.id) ?? [];
            if (createdWindowIds.length > 0 && selectedProviders.length > 0) {
              await updateDriverWindowProviders({
                weekdayWindowIds: createdWindowIds,
                providerIds: selectedProviders,
              });
            }
          }
          message.success('Time slot added successfully');
        }
        handleClose();
      } catch (error) {
        const errorMessage = formatError(error, 'Save Time Slot');
        logError(error, 'Save Time Slot');
        message.error(`Failed to save time slot: ${errorMessage}`);
      }
    }
  };

  const handleAddPeruDefaultSlots = async () => {
    try {
      const result = await insertDefaultDriverWindowSlots({
        driverId: state.selectedDriverId ?? '',
      });
      if (result?.insert_driver_window?.affected_rows === 0) {
        message.info('No new slots to add');
      } else {
        message.success('Default slots for Peru added successfully');
      }
      handleClose();
    } catch (error) {
      const errorMessage = formatError(error, 'Add Default Slots');
      logError(error, 'Add Default Slots');
      message.error(`Error adding default slots: ${errorMessage}`);
    }
  };

  const isLoading =
    insertDriverWindowSlotLoading || updateDriverWindowSlotLoading || updateProvidersLoading;

  return (
    <AppModal
      title={state.isEditMode ? 'Edit Time Slot' : 'Create Time Slot'}
      open={open}
      onOk={handleOk}
      onCancel={handleClose}
      okButtonProps={{
        loading: isLoading,
        disabled: selectedProviders.length === 0,
      }}
      cancelButtonProps={{
        disabled: isLoading,
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong>Time Range</Typography.Text>
        <TimePicker.RangePicker
          format="HH:mm"
          minuteStep={15}
          onChange={handleTimeRangeChange}
          value={state.selectedTimeRange}
          style={{ width: '100%', marginTop: 8 }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong>
          Delivery Providers <span style={{ color: 'red' }}>*</span>
        </Typography.Text>
        <div style={{ marginTop: 8 }}>
          <ProviderSelect
            value={selectedProviders}
            onChange={setSelectedProviders}
            disabled={isLoading}
          />
        </div>
        {selectedProviders.length === 0 && (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            At least one provider is required
          </Typography.Text>
        )}
      </div>

      <div>
        {selectedDepartment?.countryId === 'PE' && (
          <Button
            style={{ marginTop: 8 }}
            onClick={handleAddPeruDefaultSlots}
            loading={insertDefaultLoading}
          >
            Horario principal
          </Button>
        )}
      </div>
    </AppModal>
  );
};
